import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, doc, getDoc, serverTimestamp, addDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Student, User } from '../../../utils/firebase';
import { QRCodeSVG } from 'qrcode.react';
import { Timestamp } from 'firebase/firestore';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';
import CheckCircleIcon from '../../../components/icons/CheckCircleIcon';
import ItemUsageDialog from '../../../components/ui/ItemUsageDialog';
import { createApprovalNotification, createItemNotification } from '../../../utils/notifications';
import GachaScratchAnimation from '../../../components/ui/GachaScratchAnimation';
import GachaCardAnimation from '../../../components/ui/GachaCardAnimation';
import GachaMaruAnimation from '../../../components/ui/GachaMaruAnimation';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FEATURE_CHARACTER_COSMETIC } from '../../../config/featureFlags';
import {
  PET_CONSUMABLE_CATEGORY,
  PET_PLAY_TICKET_CATEGORY,
  isPetConsumableCategory,
  isPetPlayTicketCategory
} from '../../../types/petStore';

// 타입 가드 함수
const isStudent = (user: User): user is Student => {
  return user.role === 'student';
};

interface StoreItem {
  id: string;
  name: string;
  description: string;
  type: 'point' | 'stamp';
  targetType: 'class' | 'subject';
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  stock: number;
  isLimited: boolean;
  salePeriod?: {
    start: Date;
    end: Date;
  };
  itemCategory: 'usage' | 'qr' | 'seat' | 'collectible' | 'gacha';
  isUnlimitedStock: boolean;
  imageUrl?: string;
  requiresApproval?: boolean;
}

interface MyItem {
  id: string;
  itemId?: string;
  name?: string;
  description?: string;
  type?: string;
  targetType?: 'class' | 'subject';
  itemCategory?: string;
  quantity?: number;
  purchasedAt?: Timestamp;
  lastUsedAt?: Timestamp;
  subjectId?: string;
  image?: string;
  expiryDate?: string;
  uniqueKey: string;
  status?: 'available' | 'pending' | 'used';
  gachaItems?: Array<{ itemId?: string; itemName: string; count: number; imageUrl?: string; isBlank?: boolean }>;
  selectedGachaItem?: { itemId?: string; itemName: string; imageUrl?: string; isBlank?: boolean }; // 구매 시 확정된 뽑기 결과
  gachaAnimationType?: 'scratch' | 'card' | 'maru';
  scratchImageUrl?: string; // 스크래치 레이어 이미지 URL
}

type StatusFilter = 'all' | 'pending' | 'available' | 'used';

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: '전체' },
  { value: 'pending', label: '승인 대기' },
  { value: 'available', label: '미사용' },
  { value: 'used', label: '사용 완료' },
];

const ITEM_CATEGORY_FILTERS: Array<{ value: string; label: string }> = [
  { value: 'all', label: '모든 아이템' },
  { value: 'usage', label: '사용권' },
  { value: 'qr', label: 'QR 코드' },
  { value: 'seatReservation', label: '좌석 예약권' },
  { value: 'seatTakeover', label: '좌석 강탈권' },
  { value: 'collectible', label: '소장용' },
  { value: 'gacha', label: '뽑기' },
  { value: PET_CONSUMABLE_CATEGORY, label: '캐릭터 간식' },
  { value: PET_PLAY_TICKET_CATEGORY, label: '놀아주기 이용권' },
  ...(FEATURE_CHARACTER_COSMETIC ? [{ value: 'cosmetic', label: '캐릭터 복장' }] : []),
  { value: 'furniture', label: '마이홈 가구' },
  { value: 'roomSurface', label: '벽지·바닥' },
];

const getItemCategoryLabel = (category?: string): string => {
  if (category === 'qr') return 'QR 코드';
  if (category === 'seat' || category === 'seatReservation') return '좌석 예약권';
  if (category === 'seatTakeover') return '좌석 강탈권';
  if (category === 'collectible') return '소장용';
  if (category === 'gacha') return '뽑기';
  if (isPetConsumableCategory(category)) return '캐릭터 간식';
  if (isPetPlayTicketCategory(category)) return '놀아주기 이용권';
  if (category === 'cosmetic') return '캐릭터 복장';
  if (category === 'furniture') return '마이홈 가구';
  if (category === 'roomSurface') return '벽지·바닥';
  return '사용권';
};

const matchesItemCategory = (itemCategory: string | undefined, filter: string): boolean => {
  if (filter === 'all') return true;
  if (filter === 'seatReservation' || filter === 'seat') {
    return itemCategory === 'seatReservation' || itemCategory === 'seat';
  }
  return itemCategory === filter;
};

const matchesItemStatus = (status: string | undefined, filter: StatusFilter): boolean => {
  if (filter === 'all') return true;
  if (filter === 'available') return status === 'available' || !status;
  return status === filter;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`item-tabpanel-${index}`}
      aria-labelledby={`item-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div style={{ padding: '24px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

const ClassMyItems: React.FC = () => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState<MyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 탭 기능 제거 (학급 아이템만 표시)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isSubmittingUsage, setIsSubmittingUsage] = useState(false);
  // 상태 반영 전에 버튼을 연달아 눌러도 요청이 한 번만 가도록 막는다.
  const usageSubmittingRef = useRef(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MyItem | null>(null);
  const [imageLoadErrors, setImageLoadErrors] = useState<{[key: string]: boolean}>({});
  const [imageLoadAttempts, setImageLoadAttempts] = useState<{[key: string]: number}>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [gachaAnimationOpen, setGachaAnimationOpen] = useState(false);
  const [gachaResult, setGachaResult] = useState<{ itemId?: string; itemName: string; imageUrl?: string; isBlank?: boolean; selectedIndex?: number } | null>(null);
  const [pendingGachaItem, setPendingGachaItem] = useState<MyItem | null>(null);
  
  // 모바일 감지 상태
  const [isMobile, setIsMobile] = useState(false);
  
  // 페이지네이션 상태 추가
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [filterCategory, setFilterCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filteredItems = (items || []).filter((item) =>
    matchesItemCategory(item.itemCategory, filterCategory) &&
    matchesItemStatus(item.status, statusFilter)
  );

  // 페이지네이션 계산
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = isMobile ? filteredItems : filteredItems.slice(startIndex, endIndex);

  const handleCategoryFilterChange = (category: string) => {
    setFilterCategory(category);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status: StatusFilter) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };
  
  // 페이지네이션 함수들
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // 모바일 감지 useEffect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // 토스트 알림 자동 닫기
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  useEffect(() => {
      if (!currentUser) {
        setError('로그인이 필요합니다.');
        return;
      }

      if (!currentUser.schoolId || !currentUser.classId) {
        setError('학교 또는 반 정보가 없습니다.');
        return;
      }

    let unsubscribeStudent: (() => void) | null = null;

    const mapStudentItems = (studentItems: Record<string, any> | undefined) => {
      const mapped: MyItem[] = Object.entries(studentItems || {})
        .filter(([_, value]: [string, any]) => value && (value.targetType === 'class' || !value.targetType))
        .map(([key, value]: [string, any]) => ({
              ...value,
          id: key,
              itemId: value?.itemId || value?.id || '',
              uniqueKey: key,
              quantity: 1,
              name: value?.name || '이름 없음',
              description: value?.description || '설명이 없습니다.',
              image: value?.image || 'https://via.placeholder.com/150',
              status: value?.status || 'available',
              itemCategory: value?.itemCategory || 'usage',
              targetType: value?.targetType || 'class',
              gachaItems: value?.gachaItems,
              gachaAnimationType: value?.gachaAnimationType,
              scratchImageUrl: value?.scratchImageUrl
        }));

      console.log('매핑된 아이템:', { mapped });
      setItems(mapped);
    };

    const studentRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students/${currentUser.uid}`);

    const initialize = async () => {
      try {
        console.log('currentUser 정보:', {
          uid: currentUser.uid,
          schoolId: currentUser.schoolId,
          classId: currentUser.classId,
          role: currentUser.role
        });

        // 초기에 한 번 학생 문서 존재 여부 확인
        const snapshot = await getDoc(studentRef);
        if (!snapshot.exists()) {
          throw new Error('학생 정보를 찾을 수 없습니다.');
        }

        // 학급 상점 아이템 정보는 필요하면 추후 활용 (현재는 student 아이템 정보만 사용)
        const studentData = snapshot.data();
        console.log('학생 데이터:', studentData);
        mapStudentItems(studentData?.items);
        setError(null);

        // 실시간 구독 설정
        unsubscribeStudent = onSnapshot(studentRef, (studentSnap) => {
          if (studentSnap.exists()) {
            const realtimeData = studentSnap.data();
            console.log('학생 데이터 실시간 업데이트:', realtimeData);
            mapStudentItems(realtimeData?.items);
          }
        });
      } catch (err) {
        console.error('Error fetching items:', err);
        setError('아이템 정보를 불러오는 중 오류가 발생했습니다. 권한이 부족할 수 있습니다.');
      } finally {
        setLoading(false);
      }
    };

    initialize();

    return () => {
      if (unsubscribeStudent) {
        unsubscribeStudent();
      }
    };
  }, [currentUser]);

  // 승인 요청 상태 실시간 감시 - 제거됨 (교사가 승인할 때 바로 삭제하도록 변경)

  // 탭 변경 핸들러 제거 (학급 아이템만 표시)

  const handleUseItem = (item: MyItem) => {
    if (isPetConsumableCategory(item.itemCategory)) {
      setSnackbar({
        open: true,
        message: '캐릭터 간식은 캐릭터 허브에서 사용할 수 있어요.',
        severity: 'success'
      });
      return;
    }

    if (isPetPlayTicketCategory(item.itemCategory)) {
      setSnackbar({
        open: true,
        message: '놀아주기 이용권은 캐릭터 허브에서 사용할 수 있어요.',
        severity: 'success'
      });
      return;
    }

    // 이미 승인 대기 중인지 확인
    if (item.status === 'pending') {
      console.log('이미 승인 대기 중인 아이템입니다:', item.name);
      return;
    }
    
    setSelectedItem(item);
    if (item.itemCategory === 'usage') {
      setConfirmDialogOpen(true);
    } else if (item.itemCategory === 'qr') {
      setQrDialogOpen(true);
    } else if (item.itemCategory === 'gacha') {
      // 뽑기 아이템은 바로 사용 (확인 다이얼로그 없이)
      // 구매 시점에 이미 확정된 항목 사용
      if (!item.selectedGachaItem) {
        alert('뽑기 아이템 정보가 올바르지 않습니다.');
        return;
      }

      // 애니메이션 표시 (구매 시점에 확정된 결과)
      const result = {
        itemId: item.selectedGachaItem.itemId,
        itemName: item.selectedGachaItem.itemName,
        imageUrl: item.selectedGachaItem.imageUrl,
        isBlank: item.selectedGachaItem.isBlank
      };
      setGachaResult(result);
      setPendingGachaItem(item);
      setGachaAnimationOpen(true);
      setSelectedItem(null);
    }
  };

  const handleConfirmUseItem = async () => {
    if (!selectedItem || !currentUser) return;
    if (usageSubmittingRef.current) return;
    if (selectedItem.status === 'pending') {
      setConfirmDialogOpen(false);
      setSelectedItem(null);
      return;
    }
    usageSubmittingRef.current = true;
    setIsSubmittingUsage(true);

    // 일반 사용 아이템인 경우 (기존 로직)
    try {
      console.log('currentUser 정보:', currentUser);
      console.log('아이템 사용 승인 요청을 생성합니다.', {
        itemId: selectedItem.id,
        itemName: selectedItem.name,
        studentId: currentUser.uid,
        studentName: currentUser.name,
        targetType: selectedItem.targetType,
        studentClassId: (currentUser as Student).classId,
        studentGrade: (currentUser as Student).grade,
        studentClass: (currentUser as Student).class
      });
      
      // 학생 문서 조회 및 아이템 상태 업데이트
      const studentRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students/${currentUser.uid}`);
      const studentSnap = await getDoc(studentRef);
      let teacherId: string | undefined = (currentUser as Student).teacherId;
      let teacherName: string | undefined;
      if (studentSnap.exists()) {
        const studentDocData = studentSnap.data();
        teacherId = teacherId || studentDocData.teacherId;
        teacherName = studentDocData.teacherName;
      }
      if (!currentUser.schoolId || !currentUser.classId || !selectedItem.uniqueKey) {
        throw new Error('아이템 정보가 없습니다.');
      }
      const functions = getFunctions(getApp());
      const markPending = httpsCallable<
        { schoolId: string; classId: string; inventoryKey: string },
        { ok: boolean; alreadyPending?: boolean }
      >(functions, 'markOwnedItemPending');
      const markResult = await markPending({
        schoolId: currentUser.schoolId,
        classId: currentUser.classId,
        inventoryKey: selectedItem.uniqueKey,
      });
      if (markResult.data?.alreadyPending) {
        // 다른 탭이나 이전 요청으로 이미 승인 대기 중이면 요청을 새로 만들지 않는다.
        setItems(prevItems =>
          prevItems.map(item =>
            item.id === selectedItem.id ? { ...item, status: 'pending' as const } : item
          )
        );
        setSnackbar({
          open: true,
          message: '이미 승인 요청을 보낸 아이템이에요. 선생님의 승인을 기다려 주세요.',
          severity: 'success'
        });
        setConfirmDialogOpen(false);
        setSelectedItem(null);
        return;
      }

      // 학급 아이템은 서버(notifyTeacherOnItemApproval)가 담임 알림을 만든다.
      // 교과 아이템만 기존처럼 화면에서 담당 교사를 찾아 알림을 보낸다.
      const sendsTeacherNoticeFromClient = selectedItem.targetType !== 'class';
      if (sendsTeacherNoticeFromClient) {
        // 담임 교사 정보 확인
        const classRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}`);
        const classDoc = await getDoc(classRef);
        if (classDoc.exists()) {
          teacherId = teacherId || classDoc.data().teacherUid || classDoc.data().teacherId;
          teacherName = teacherName || classDoc.data().teacherName;
        }

        // teacherId가 존재하더라도 실제 auth UID인지 확인
        if (teacherId && currentUser.schoolId) {
          try {
            const teacherDoc = await getDoc(doc(db, `schools/${currentUser.schoolId}/teachers/${teacherId}`));
            if (teacherDoc.exists()) {
              const teacherData = teacherDoc.data();
              teacherName = teacherName || teacherData.name || teacherData.teacherName || teacherData.displayName;
              teacherId = teacherData.uid || teacherId;
            }
          } catch (error) {
            console.warn('schools/{schoolId}/teachers 에서 담임 정보를 확인하는 중 오류:', error);
          }
        }

        // 담임 정보가 없다면 teachers 컬렉션에서 조회 (권한 여부에 따라 실패할 수 있음)
        if (!teacherId && currentUser.schoolId && currentUser.classId) {
          try {
            const teacherQuery = query(
              collection(db, `schools/${currentUser.schoolId}/teachers`),
              where('classId', '==', currentUser.classId)
            );
            const teacherSnapshot = await getDocs(teacherQuery);
            if (!teacherSnapshot.empty) {
              const teacherDoc = teacherSnapshot.docs[0];
              const teacherData = teacherDoc.data();
              teacherId = teacherData.uid || teacherDoc.id;
              teacherName = teacherName || teacherData.name || teacherData.teacherName || teacherData.displayName;
            }
          } catch (error) {
            console.warn('담임 교사를 조회하는 중 권한 오류가 발생했습니다:', error);
          }
        }
      }
      
      // 승인 요청 생성
      const approvalRef = collection(db, 
        selectedItem.targetType === 'class' 
          ? `schools/${currentUser.schoolId}/classes/${currentUser.classId}/itemApprovals`
          : `schools/${currentUser.schoolId}/subjects/${selectedItem.subjectId}/subjectitemApprovals`
      );
      console.log('승인 요청 데이터:', {
        studentId: currentUser.uid || '',
        studentName: currentUser.name || '학생',
        itemId: selectedItem.uniqueKey || '',
        itemName: selectedItem.name || '',
        status: 'pending',
        reason: '아이템 사용 요청',
        targetType: selectedItem.targetType || '',
        studentClassId: (currentUser as Student).classId || '',
        studentGrade: (currentUser as Student).grade || '',
        studentClass: (currentUser as Student).class || ''
      });
      
      const approvalDoc = await addDoc(approvalRef, {
        studentId: currentUser.uid || '',
        studentName: currentUser.name || '학생',
        itemId: selectedItem.uniqueKey || '',
        itemName: selectedItem.name || '',
        status: 'pending',
        createdAt: serverTimestamp(),
        reason: '아이템 사용 요청',
        targetType: selectedItem.targetType || '',
        studentClassId: (currentUser as Student).classId || '',
        studentGrade: (currentUser as Student).grade || '',
        studentClass: (currentUser as Student).class || ''
      });

      // 교사에게 알림 전송 (교과 아이템만. 학급 아이템은 서버 트리거가 보낸다)
      if (sendsTeacherNoticeFromClient && teacherId) {
        console.log('승인 요청 알림 전송 대상 교사 UID:', teacherId, 'teacherName:', teacherName);
        try {
          await createApprovalNotification(
            teacherId,
            currentUser.name || '학생',
            selectedItem.name || '아이템',
            approvalDoc.id,
            currentUser.schoolId,
            currentUser.classId || '',
            (currentUser as Student).grade,
            (currentUser as Student).class
          );
        } catch (notificationError) {
          console.error('승인 요청 알림 생성 중 오류:', notificationError);
        }
      } else if (sendsTeacherNoticeFromClient) {
        console.warn('담임 교사를 찾을 수 없어 알림을 전송하지 못했습니다.', {
          schoolId: currentUser.schoolId,
          classId: currentUser.classId,
          selectedItem
        });
      }

      // 학생 본인에게도 알림 전송
      try {
        await createItemNotification(
          currentUser.uid,
          '아이템 사용 승인 요청',
          `${selectedItem.name || '아이템'} 사용 승인 요청이 교사에게 전송되었습니다. 승인이 되면 자동으로 보유 항목에서 차감됩니다.`,
          currentUser.schoolId,
          currentUser.classId || '',
          'medium',
          '/class/student/ClassMyItems',
          {
            itemId: selectedItem.uniqueKey,
            itemName: selectedItem.name,
            approvalRequestId: approvalDoc.id
          }
        );
      } catch (studentNotificationError) {
        console.error('학생 알림 생성 중 오류:', studentNotificationError);
      }
      
      // UI 업데이트 (id로 정확한 아이템만 업데이트)
      setItems(prevItems => 
        prevItems.map(item => 
          item.id === selectedItem.id 
            ? { ...item, status: 'pending' as const }
            : item
        )
      );
      
      console.log('승인 요청이 성공적으로 생성되었습니다. ID:', approvalDoc.id);
      console.log('승인 요청 경로:', approvalRef.path);
      
      // 상태 업데이트
      setSnackbar({
        open: true,
        message: '아이템 사용 승인 요청이 교사에게 전송되었습니다. 승인이 되면 자동으로 보유 항목에서 차감됩니다.',
        severity: 'success'
      });
      setConfirmDialogOpen(false);
      setSelectedItem(null);
    } catch (err) {
      console.error('Error using item:', err);
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : '아이템 사용 중 오류가 발생했습니다. 권한이 부족할 수 있습니다.',
        severity: 'error'
      });
    } finally {
      usageSubmittingRef.current = false;
      setIsSubmittingUsage(false);
    }
  };

  const generateQrData = (item: MyItem) => {
    return JSON.stringify({
      itemId: item.id,
      studentId: currentUser?.uid,
      timestamp: Date.now(),
      type: item.type,
      name: item.name
    });
  };

  // 뽑기 결과 처리 함수 (애니메이션 완료 후 호출)
  const processGachaResult = async (result: { itemId?: string; itemName: string; imageUrl?: string; isBlank?: boolean; selectedIndex?: number }, gachaItem: MyItem) => {
    if (!currentUser || !isStudent(currentUser) || !gachaItem.uniqueKey) return;

    try {
      setError(null);
      if (!currentUser.schoolId || !currentUser.classId || !gachaItem.uniqueKey) {
        throw new Error('뽑기 정보가 없습니다.');
      }
      const functions = getFunctions(getApp());
      const resolve = httpsCallable(functions, 'resolveOwnedGacha');
      await resolve({
        schoolId: currentUser.schoolId,
        classId: currentUser.classId,
        inventoryKey: gachaItem.uniqueKey,
      });
    } catch (err) {
      console.error('뽑기 결과 처리 중 오류:', err);
      setError(err instanceof Error ? err.message : '뽑기 결과 처리에 실패했습니다.');
    } finally {
      setGachaAnimationOpen(false);
      setPendingGachaItem(null);
      setGachaResult(null);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div>로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div style={{ color: 'red', padding: '20px', border: '1px solid red', borderRadius: '4px' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%',
      height: isMobile ? '100%' : 'auto',
      minHeight: isMobile ? 'auto' : 'fit-content',
      paddingLeft: isMobile ? '20px' : '0',
      paddingRight: isMobile ? '20px' : '0',
      display: isMobile ? 'flex' : 'block',
      flexDirection: isMobile ? 'column' : undefined,
      overflow: isMobile ? 'hidden' : 'visible',
    }}>
      {/* 헤더 영역 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
        flexShrink: 0,
      }}>
        {/* 제목 */}
        <h1 style={{
          color: 'var(--Color-text-normal, #161616)',
          fontSize: isMobile ? '24px' : '32px',
          fontFamily: 'Pretendard',
          fontWeight: '600',
          lineHeight: isMobile ? '36px' : '48px',
          margin: 0
        }}>
          내 아이템
        </h1>
      </div>

      {/* 메인 컨텐츠 영역 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '16px' : '20px',
        flex: isMobile ? 1 : undefined,
        minHeight: isMobile ? 0 : undefined,
        overflow: isMobile ? 'hidden' : undefined,
      }}>
        {/* 아이템 개수 및 필터 섹션 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '12px' : '16px',
          flexShrink: 0,
        }}>
          {/* 아이템 개수 표시 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
          }}>
            <span style={{
              fontSize: isMobile ? '16px' : '18px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: isMobile ? '24px' : '27px',
              color: 'var(--Color-text-normal, #161616)'
            }}>
              현재
            </span>
            <span style={{
              fontSize: isMobile ? '16px' : '18px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: isMobile ? '24px' : '27px',
              color: 'var(--Color-fill-brand-color, #FF6F0F)'
            }}>
              {items ? items.length : 0}
            </span>
            <span style={{
              fontSize: isMobile ? '16px' : '18px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: isMobile ? '24px' : '27px',
              color: 'var(--Color-text-normal, #161616)'
            }}>
              개의 아이템을 보유하고 있습니다
            </span>
          </div>
          
          {/* 상태 필터 */}
          <div
            role="group"
            aria-label="아이템 상태 필터"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '8px' : '12px',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}
          >
            {STATUS_FILTERS.map((filter) => {
              const isSelected = statusFilter === filter.value;
              return (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => handleStatusFilterChange(filter.value)}
                  aria-pressed={isSelected}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    cursor: 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    fontFamily: 'Pretendard',
                  }}
                >
                  <CheckCircleIcon
                    width={16}
                    height={16}
                    color={isSelected ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)'}
                  />
                  <span style={{
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '18px',
                    color: isSelected ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)'
                  }}>
                    {filter.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* 유형 필터 */}
          <div
            role="group"
            aria-label="아이템 유형 필터"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              overflowX: isMobile ? 'auto' : 'visible',
              overflowY: 'hidden',
              width: '100%',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              WebkitOverflowScrolling: isMobile ? 'touch' : 'auto',
              scrollbarWidth: isMobile ? 'none' : 'auto',
            } as React.CSSProperties}
          >
            {isMobile && (
              <style>{`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
            )}
            {ITEM_CATEGORY_FILTERS.map((category) => {
              const isSelected = filterCategory === category.value;
              return (
                <button
                  key={category.value}
                  type="button"
                  onClick={() => handleCategoryFilterChange(category.value)}
                  aria-pressed={isSelected}
                  style={{
                    height: '32px',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    background: isSelected ? 'var(--Color-fill-normal, #293044)' : 'var(--Color-background-normal, white)',
                    borderRadius: '8px',
                    border: isSelected ? 'none' : '1px solid var(--Color-line-normal, #F0F0F0)',
                    color: isSelected ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '18px',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  onMouseOver={(e) => {
                    if (!isSelected) e.currentTarget.style.background = 'var(--Color-fill-hover, #F5F5F5)';
                  }}
                  onMouseOut={(e) => {
                    if (!isSelected) e.currentTarget.style.background = 'var(--Color-background-normal, white)';
                  }}
                >
                  {category.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 아이템 카드 목록 */}
        <div style={{
            display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(243px, 1fr))' : 'repeat(5, 1fr)',
          gap: isMobile ? '12px' : '16px',
          padding: isMobile ? '0' : '20px 0',
          flex: isMobile ? 1 : undefined,
          minHeight: isMobile ? 0 : undefined,
          overflowY: isMobile ? 'auto' : undefined,
          justifyContent: isMobile ? 'center' : undefined,
          justifyItems: isMobile ? 'center' : undefined,
          width: isMobile ? '100%' : '1280px',
          height: isMobile ? 'auto' : '595px',
          maxWidth: isMobile ? '100%' : '1280px',
        }}>
        {currentItems.map((item, index) => {
            const isPending: boolean = item.status === 'pending';
            
            return (
            <div 
                key={item.uniqueKey}
              onClick={() => handleUseItem(item)}
              style={{
                width: '243px', 
                height: '290px', 
                minHeight: '290px',
                position: 'relative', 
                background: 'var(--Color-background-normal, white)', 
                boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)', 
                overflow: 'hidden', 
                borderRadius: '12px',
                cursor: item.status === 'pending' ? 'not-allowed' : 'pointer',
                opacity: item.status === 'pending' ? 0.7 : 1,
                transition: 'all 0.2s ease',
                border: '1px solid var(--Color-line-normal, #F0F0F0)'
              }}
            >
              {/* 이미지 영역 */}
              <div style={{
                width: '100%', 
                height: 112, 
                minHeight: 112,
                position: 'relative', 
                background: 'var(--Color-background-sub, #F8F8F8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img
                  src={imageLoadErrors[item.id] ? 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=' : item.image}
                  alt={item.name}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover' 
                  }}
                  onError={(e) => {
                    const attempts = imageLoadAttempts[item.id] || 0;
                    if (attempts < 1) {
                      console.error(`이미지 로딩 실패: ${item.image}`);
                      setImageLoadErrors(prev => ({...prev, [item.id]: true}));
                      setImageLoadAttempts(prev => ({...prev, [item.id]: attempts + 1}));
                    }
                  }}
                  onLoad={() => {
                    if (!imageLoadErrors[item.id]) {
                      console.log(`이미지 로딩 성공: ${item.image}`);
                      setImageLoadErrors(prev => ({...prev, [item.id]: false}));
                    }
                  }}
                />
                
                {/* 상태 배지 */}
                    {isPending && (
                  <div style={{
                    position: 'absolute',
                    top: isMobile ? 8 : 16,
                    left: isMobile ? 12 : 20,
                    justifyContent: 'flex-start', 
                    alignItems: 'center', 
                    display: 'flex'
                  }}>
                    <div style={{
                      paddingLeft: isMobile ? 8 : 12, 
                      paddingRight: isMobile ? 8 : 12, 
                      paddingTop: isMobile ? 1 : 2, 
                      paddingBottom: isMobile ? 1 : 2, 
                      background: '#FFD2B5', 
                      borderRadius: isMobile ? 6 : 8, 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      gap: 4, 
                      display: 'flex'
                    }}>
                      <div style={{
                        color: 'var(--Color-text-brand-color, #FF6F0F)', 
                        fontSize: isMobile ? '10px' : '12px', 
                        fontFamily: 'Pretendard', 
                        fontWeight: '500', 
                        lineHeight: isMobile ? '14px' : '18px', 
                        wordWrap: 'break-word'
                      }}>
                        승인 대기 중
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 아이템 정보 */}
              <div style={{
                padding: isMobile ? '12px' : '20px', 
                flexDirection: 'column', 
                justifyContent: 'flex-start', 
                alignItems: 'flex-start', 
                gap: isMobile ? 12 : 20, 
                  display: 'flex',
                height: isMobile ? 'auto' : 'calc(100% - 112px)',
                minHeight: isMobile ? 'auto' : undefined,
                boxSizing: 'border-box'
              }}>
                {/* 아이템 제목과 설명 */}
                <div style={{
                  flexDirection: 'column',
                  justifyContent: 'flex-start', 
                  alignItems: 'flex-start', 
                  gap: isMobile ? 6 : 8, 
                  display: 'flex'
                }}>
                  <div style={{
                    color: 'var(--Color-text-normal, #161616)', 
                    fontSize: isMobile ? '14px' : '18px', 
                    fontFamily: 'Pretendard', 
                    fontWeight: '600', 
                    lineHeight: isMobile ? '20px' : '27px', 
                    wordWrap: 'break-word'
                  }}>
                    {item.name || '이름 없음'}
                  </div>
                  <div style={{
                    color: 'var(--Color-text-assistive-strong, #4C4C4C)', 
                    fontSize: isMobile ? '11px' : '13px', 
                    fontFamily: 'Pretendard', 
                    fontWeight: '500', 
                    lineHeight: isMobile ? '16px' : '19.5px', 
                    wordWrap: 'break-word'
                  }}>
                    {item.description && item.description.length > (isMobile ? 60 : 60)
                      ? `${item.description.substring(0, isMobile ? 60 : 60)}...` 
                      : item.description || '설명이 없습니다.'}
                  </div>
                </div>

                {/* 아이템 세부 정보 */}
                <div style={{
                  flexDirection: 'column', 
                  justifyContent: 'flex-start', 
                  alignItems: 'flex-start', 
                  gap: isMobile ? 6 : 8, 
                  display: 'flex'
                }}>
                  <div style={{
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    gap: isMobile ? 6 : 8, 
                    display: 'flex'
                  }}>
                    <div style={{
                      width: isMobile ? '40px' : '60px', 
                      color: 'var(--Color-text-assistive, #989898)', 
                      fontSize: isMobile ? '11px' : '13px', 
                      fontFamily: 'Pretendard', 
                      fontWeight: '500', 
                      lineHeight: isMobile ? '16px' : '19.5px', 
                      wordWrap: 'break-word'
                    }}>
                      유형
                    </div>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)', 
                      fontSize: isMobile ? '11px' : '13px', 
                      fontFamily: 'Pretendard', 
                      fontWeight: '500', 
                      lineHeight: isMobile ? '16px' : '19.5px', 
                      wordWrap: 'break-word'
                    }}>
                      {getItemCategoryLabel(item.itemCategory)}
                    </div>
                  </div>
                  <div style={{
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    gap: isMobile ? 6 : 8, 
                    display: 'flex'
                  }}>
                    <div style={{
                      width: isMobile ? '40px' : '60px', 
                      color: 'var(--Color-text-assistive, #989898)', 
                      fontSize: isMobile ? '11px' : '13px', 
                      fontFamily: 'Pretendard', 
                      fontWeight: '500', 
                      lineHeight: isMobile ? '16px' : '19.5px', 
                      wordWrap: 'break-word'
                    }}>
                      구매일
                    </div>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)', 
                      fontSize: isMobile ? '11px' : '13px', 
                      fontFamily: 'Pretendard', 
                      fontWeight: '500', 
                      lineHeight: isMobile ? '16px' : '19.5px', 
                      wordWrap: 'break-word'
                    }}>
                      {item.purchasedAt 
                        ? (item.purchasedAt instanceof Timestamp
                            ? item.purchasedAt.toDate().toLocaleDateString('ko-KR')
                            : new Date(item.purchasedAt).toLocaleDateString('ko-KR'))
                        : '2025.06.23'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
          
          {/* 빈 상태 메시지 */}
          {filteredItems.length === 0 && (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              color: 'var(--Color-text-assistive, #989898)',
              fontSize: '14px',
              fontFamily: 'Pretendard',
              fontWeight: '500',
              padding: '60px 20px'
            }}>
              {(!items || items.length === 0)
                ? '보유한 아이템이 없습니다.'
                : '해당 조건의 아이템이 없습니다.'}
            </div>
          )}
        </div>

        {/* 페이지네이션 */}
        {filteredItems.length > 0 && !isMobile && (
          <div style={{
            padding: isMobile ? '16px 0' : '20px 0',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? '4px' : '8px',
            flexShrink: 0,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
          }}>
            {/* 맨 처음으로 */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div 
                style={{ 
                  cursor: currentPage === 1 ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? '20px' : '24px',
                  height: isMobile ? '20px' : '24px'
                }}
                onClick={currentPage === 1 ? undefined : goToFirstPage}
              >
                <LeftArrowDoubleIcon 
                  width={isMobile ? 20 : 24} 
                  height={isMobile ? 20 : 24} 
                  color={currentPage === 1 ? '#989898' : '#161616'}
                />
              </div>
              {/* 이전 페이지 */}
              <div 
                style={{ 
                  cursor: currentPage === 1 ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? '20px' : '24px',
                  height: isMobile ? '20px' : '24px'
                }}
                onClick={currentPage === 1 ? undefined : goToPreviousPage}
              >
                <LeftArrowIcon 
                  width={isMobile ? 20 : 24} 
                  height={isMobile ? 20 : 24} 
                  color={currentPage === 1 ? '#989898' : '#161616'}
                />
              </div>
            </div>
            
            {/* 페이지 번호들 - 모바일에서는 최대 5개만 표시 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '2px' : '0' }}>
              {(() => {
                if (isMobile && totalPages > 5) {
                  // 모바일: 현재 페이지 주변 5개만 표시
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((pageNum) => (
                    <div 
                      key={pageNum} 
                      style={{
                        width: isMobile ? '20px' : '24px',
                        height: isMobile ? '20px' : '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => goToPage(pageNum)}
                    >
                      <span style={{
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '500',
                        color: currentPage === pageNum ? '#161616' : '#989898',
                        lineHeight: isMobile ? '12px' : '14px'
                      }}>
                        {pageNum}
                      </span>
                    </div>
                  ));
                } else {
                  // 데스크톱: 모든 페이지 표시
                  return [...Array(totalPages)].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <div 
                        key={pageNum} 
                        style={{
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => goToPage(pageNum)}
                      >
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: currentPage === pageNum ? '#161616' : '#989898',
                          lineHeight: '14px'
                        }}>
                          {pageNum}
                        </span>
                      </div>
                    );
                  });
                }
              })()}
            </div>
            
            {/* 다음으로 */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {/* 다음 페이지 */}
              <div 
                style={{ 
                  cursor: currentPage === totalPages ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? '20px' : '24px',
                  height: isMobile ? '20px' : '24px'
                }}
                onClick={currentPage === totalPages ? undefined : goToNextPage}
              >
                <RightArrowIcon 
                  width={isMobile ? 20 : 24} 
                  height={isMobile ? 20 : 24} 
                  color={currentPage === totalPages ? '#989898' : '#161616'}
                />
              </div>
              {/* 맨 끝으로 */}
              <div 
                style={{ 
                  cursor: currentPage === totalPages ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? '20px' : '24px',
                  height: isMobile ? '20px' : '24px'
                }}
                onClick={currentPage === totalPages ? undefined : goToLastPage}
              >
                <RightArrowDoubleIcon 
                  width={isMobile ? 20 : 24} 
                  height={isMobile ? 20 : 24} 
                  color={currentPage === totalPages ? '#989898' : '#161616'}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      
      {/* 아이템 사용 확인 다이얼로그 */}
       <ItemUsageDialog
         isOpen={confirmDialogOpen}
        onClose={() => {
          if (usageSubmittingRef.current) return;
          setConfirmDialogOpen(false);
        }}
         onConfirm={handleConfirmUseItem}
         isSubmitting={isSubmittingUsage}
         item={selectedItem}
       />
      
      {/* QR 코드 다이얼로그 */}
      {qrDialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: isMobile ? '24px' : '32px',
            borderRadius: '16px',
            maxWidth: '480px',
            width: '90%',
            maxHeight: isMobile ? '90vh' : 'none',
            overflowY: isMobile ? 'auto' : 'visible',
            textAlign: 'center',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)'
          }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)', 
              fontSize: isMobile ? '20px' : '24px', 
              fontFamily: 'Pretendard', 
              fontWeight: '600', 
              lineHeight: isMobile ? '30px' : '36px', 
              marginBottom: '8px'
            }}>
              QR 코드
            </div>
            <div style={{ 
              color: 'var(--Color-text-normal, #161616)', 
              fontSize: isMobile ? '14px' : '16px', 
              fontFamily: 'Pretendard', 
              fontWeight: '500', 
              lineHeight: isMobile ? '21px' : '24px', 
              marginBottom: '8px' 
            }}>
              {selectedItem?.name}
            </div>
            <div style={{ 
              color: 'var(--Color-text-assistive-strong, #4C4C4C)', 
              fontSize: isMobile ? '12px' : '14px', 
              fontFamily: 'Pretendard', 
              fontWeight: '400', 
              lineHeight: isMobile ? '18px' : '20px', 
              marginBottom: isMobile ? '16px' : '24px' 
            }}>
              교사에게 QR 코드를 보여주세요
            </div>
            <div style={{ 
              padding: isMobile ? '16px' : '20px', 
              backgroundColor: 'var(--Color-background-sub, #F8F8F8)', 
              borderRadius: '12px',
              display: 'inline-block',
              marginBottom: isMobile ? '16px' : '24px'
            }}>
              {selectedItem && currentUser && (
                <QRCodeSVG
                  value={generateQrData(selectedItem)}
                  size={isMobile ? 160 : 200}
                  level="H"
                  includeMargin={true}
                />
              )}
            </div>
            <div>
              <button
                onClick={() => setQrDialogOpen(false)}
                style={{
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: 'var(--Color-fill-brand-color, #FF6F0F)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: isMobile ? '13px' : '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500'
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 뽑기 애니메이션 */}
      {gachaAnimationOpen && gachaResult && pendingGachaItem && (
        <>
          {pendingGachaItem.gachaAnimationType === 'scratch' && (
            <GachaScratchAnimation
              isOpen={gachaAnimationOpen}
              result={gachaResult}
              scratchImageUrl={pendingGachaItem.scratchImageUrl}
              onComplete={(result) => {
                processGachaResult(result, pendingGachaItem);
                setGachaAnimationOpen(false);
                setGachaResult(null);
                setPendingGachaItem(null);
              }}
              onClose={() => {
                // 닫기 버튼을 누르면 결과 처리 없이 다이얼로그만 닫기
                setGachaAnimationOpen(false);
                setGachaResult(null);
                setPendingGachaItem(null);
              }}
            />
          )}
          {pendingGachaItem.gachaAnimationType === 'card' && (
            <GachaCardAnimation
              isOpen={gachaAnimationOpen}
              result={gachaResult}
              onComplete={(result) => {
                processGachaResult(result, pendingGachaItem);
                setGachaAnimationOpen(false);
                setGachaResult(null);
                setPendingGachaItem(null);
              }}
            />
          )}
          {pendingGachaItem.gachaAnimationType === 'maru' && (
            <GachaMaruAnimation
              isOpen={gachaAnimationOpen}
              result={gachaResult}
              onComplete={(result) => {
                processGachaResult(result, pendingGachaItem);
                setGachaAnimationOpen(false);
                setGachaResult(null);
                setPendingGachaItem(null);
              }}
            />
          )}
          {!pendingGachaItem.gachaAnimationType && (
            <GachaScratchAnimation
              isOpen={gachaAnimationOpen}
              result={gachaResult}
              scratchImageUrl={pendingGachaItem.scratchImageUrl}
              onComplete={(result) => {
                processGachaResult(result, pendingGachaItem);
                setGachaAnimationOpen(false);
                setGachaResult(null);
                setPendingGachaItem(null);
              }}
              onClose={() => {
                // 닫기 버튼을 누르면 결과 처리 없이 다이얼로그만 닫기
                setGachaAnimationOpen(false);
                setGachaResult(null);
                setPendingGachaItem(null);
              }}
            />
          )}
        </>
      )}

      {/* 토스트 알림 */}
      {snackbar.open && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: isMobile ? 'calc(100% - 48px)' : '400px',
            maxWidth: isMobile ? 'calc(100% - 48px)' : '600px',
            padding: '16px 20px',
            background: snackbar.severity === 'success' ? '#d4edda' : '#f8d7da',
            color: snackbar.severity === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${snackbar.severity === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            fontSize: '14px',
            fontFamily: 'Pretendard',
            fontWeight: '500',
            lineHeight: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            animation: 'slideUp 0.3s ease-out',
          }}
        >
          <span style={{ flex: 1 }}>{snackbar.message}</span>
          <button
            onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}
            style={{
              background: 'none',
              border: 'none',
              color: snackbar.severity === 'success' ? '#155724' : '#721c24',
              cursor: 'pointer',
              padding: '0',
              fontSize: '20px',
              lineHeight: '1',
              fontWeight: 'bold',
              opacity: 0.7,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
          >
            ×
          </button>
          <style>{`
            @keyframes slideUp {
              from {
                transform: translateX(-50%) translateY(100%);
                opacity: 0;
              }
              to {
                transform: translateX(-50%) translateY(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default ClassMyItems; 